/**
 * 출석 (방법 3 / 2)
 *  - /go               교사: 본인 확인(처음 1번) → 지금 진행 중인 강의 선택 → (방 코드 4자리) → 출석 완료
 *  - /go/screen/:sid   강사 화면: 큰 방 코드 + 실시간 출석 인원
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Container, CssBaseline, Paper, TextField, ThemeProvider, Typography } from '@mui/material';
import { api } from '../api';
import { C, YELLOW_GRADIENT, ON_YELLOW, formatPhoneInput, yellowTheme, ymdhm } from '../common';

type Slot = { _id: string; period: string; title: string; room?: string; instructor?: string; startAt: string; endAt: string };
type Now = { method: 2 | 3; identity: { name: string; churchName?: string; phoneMasked: string } | null; slots: Slot[]; today: Array<{ slotId: string; title: string; period: string; room?: string; checkedAt: string }> };
const hm = (d: string) => ymdhm(d).slice(11);

export const AttendGoPage: React.FC = () => {
  const [params] = useSearchParams();
  const [now, setNow] = useState<Now | null>(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [slot, setSlot] = useState<Slot | null>(null);
  const [code, setCode] = useState('');
  const [done, setDone] = useState<{ title: string; period: string; room?: string; checkedAt: string; already: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => api<{ data: Now }>('/attend/now').then((r) => setNow(r.data)).catch((e) => setError(e.message));
  useEffect(() => {
    // 교사카드의 '출석하기'로 들어오면 교사카드 로그인 정보로 본인 확인
    if (params.get('from') === 'card') api('/attend/identify-card', { body: {} }).catch(() => undefined).finally(load);
    else load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const identify = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError('');
    try { await api('/attend/identify', { body: { name, phone } }); await load(); } catch (err: any) { setError(err.message); } finally { setBusy(false); }
  };
  const checkin = async (s: Slot, roomCode?: string) => {
    setBusy(true); setError('');
    try {
      const r = await api<{ data: any }>('/attend/checkin', { body: { slotId: s._id, code: roomCode } });
      setDone(r.data); setSlot(null); setCode(''); load();
    } catch (err: any) { setError(err.message); } finally { setBusy(false); }
  };
  const forget = async () => { await api('/attend/forget', { body: {} }); setDone(null); load(); };
  const choose = (s: Slot) => { setError(''); if (now?.method === 3) { setSlot(s); setCode(''); } else checkin(s); };

  return (
    <ThemeProvider theme={yellowTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100dvh', bgcolor: C.bg }}>
        <Box sx={{ background: YELLOW_GRADIENT, color: ON_YELLOW, py: 2.5, textAlign: 'center' }}>
          <Typography fontWeight={800} fontSize={20}>강의 출석</Typography>
          {now?.identity && <Typography fontSize={13.5}>{now.identity.name} · {now.identity.churchName} · {now.identity.phoneMasked}</Typography>}
        </Box>
        <Container maxWidth="xs" sx={{ py: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!now ? <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
            : done ? (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 4, borderColor: '#f0d77a', bgcolor: '#fffbea' }}>
                <Typography fontSize={52}>✅</Typography>
                <Typography fontSize={22} fontWeight={800}>{done.already ? '이미 출석했습니다' : '출석 완료'}</Typography>
                <Typography mt={1} fontWeight={700}>[{done.period}] {done.title}</Typography>
                <Typography color={C.sub}>{done.room} · {hm(done.checkedAt)}</Typography>
                <Button sx={{ mt: 3 }} variant="outlined" onClick={() => setDone(null)}>다른 강의 출석</Button>
              </Paper>
            ) : !now.identity ? (
              <Paper component="form" onSubmit={identify} variant="outlined" sx={{ p: 3, borderRadius: 4, borderColor: C.line }}>
                <Typography fontWeight={800} fontSize={18}>본인 확인 (처음 한 번)</Typography>
                <Typography fontSize={13} color={C.sub} mb={2}>BT 신청 때 등록한 이름과 휴대폰 번호를 입력하세요. 이 기기에 기억되어 다음부터는 바로 강의를 고릅니다.</Typography>
                <TextField label="이름" fullWidth value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} />
                <TextField label="휴대폰" fullWidth value={phone} onChange={(e) => setPhone(formatPhoneInput(e.target.value))} inputProps={{ inputMode: 'numeric' }} />
                <Button type="submit" fullWidth variant="contained" size="large" disabled={!name.trim() || phone.replace(/\D/g, '').length < 10 || busy} sx={{ mt: 2.5, minHeight: 50 }}>확인</Button>
              </Paper>
            ) : slot ? (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 4, borderColor: C.line, textAlign: 'center' }}>
                <Typography fontSize={13} color={C.sub}>[{slot.period}] {slot.room}</Typography>
                <Typography fontWeight={800} fontSize={19} mb={2}>{slot.title}</Typography>
                <Typography fontSize={14} mb={1}>강사 화면에 보이는 <b>4자리 방 코드</b></Typography>
                <TextField autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', fontSize: 36, letterSpacing: '0.4em', fontWeight: 800 } }} sx={{ width: 220 }} />
                <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                  <Button fullWidth variant="outlined" onClick={() => setSlot(null)}>뒤로</Button>
                  <Button fullWidth variant="contained" disabled={code.length !== 4 || busy} onClick={() => checkin(slot, code)}>출석</Button>
                </Box>
              </Paper>
            ) : (
              <Box sx={{ display: 'grid', gap: 1.25 }}>
                <Typography fontWeight={800}>지금 진행 중인 강의</Typography>
                {!now.slots.length && <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: C.sub, borderRadius: 3 }}>지금 출석할 수 있는 강의가 없습니다. 강의 시작 15분 전부터 보입니다.</Paper>}
                {now.slots.map((s) => {
                  const mine = now.today.some((t) => t.slotId === s._id);
                  return (
                    <Paper key={s._id} variant="outlined" onClick={() => !mine && choose(s)}
                      sx={{ p: 2, borderRadius: 3, cursor: mine ? 'default' : 'pointer', borderColor: mine ? '#f0d77a' : C.line, bgcolor: mine ? '#fffbea' : '#fff', '&:hover': mine ? undefined : { borderColor: '#f5c400' } }}>
                      <Typography fontSize={12.5} color={C.sub}>{s.period} · {hm(s.startAt)}–{hm(s.endAt)} {s.room && `· ${s.room}`}</Typography>
                      <Typography fontWeight={800} fontSize={17}>{s.title}</Typography>
                      <Typography fontSize={13} color={mine ? C.ok : C.sub}>{mine ? '✓ 출석함' : s.instructor ? `강사 ${s.instructor}` : '눌러서 출석'}</Typography>
                    </Paper>
                  );
                })}
                {now.today.length > 0 && (
                  <Box mt={2}>
                    <Typography fontWeight={800} mb={0.5}>오늘 내 출석</Typography>
                    {now.today.map((t) => <Typography key={t.slotId} fontSize={13.5}>✓ [{t.period}] {t.title} · {hm(t.checkedAt)}</Typography>)}
                  </Box>
                )}
                <Button size="small" sx={{ mt: 2, color: C.sub }} onClick={forget}>내가 아니에요 (다시 본인 확인)</Button>
              </Box>
            )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export const InstructorScreen: React.FC = () => {
  const { sid } = useParams();
  const [params] = useSearchParams();
  const [d, setD] = useState<any>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    const load = () => api<{ data: any }>(`/attend/screen/${sid}?k=${encodeURIComponent(params.get('k') || '')}`).then((r) => setD(r.data)).catch((e) => setError(e.message));
    load();
    const t = window.setInterval(load, 5000);
    return () => window.clearInterval(t);
  }, [sid, params]);
  return (
    <ThemeProvider theme={yellowTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100dvh', background: YELLOW_GRADIENT, color: ON_YELLOW, display: 'grid', placeItems: 'center', textAlign: 'center', p: 3 }}>
        {error ? <Alert severity="error">{error}</Alert> : !d ? <CircularProgress /> : (
          <Box>
            <Typography fontSize={{ xs: 18, md: 28 }} fontWeight={700}>[{d.period}] {d.room}</Typography>
            <Typography fontSize={{ xs: 26, md: 44 }} fontWeight={800} mb={3}>{d.title}</Typography>
            <Typography fontSize={{ xs: 16, md: 24 }}>휴대폰으로 <b>awanaevent.com/go</b> 접속 → 이 강의 선택{d.method === 3 ? ' → 방 코드 입력' : ''}</Typography>
            {d.method === 3 && (
              <Box sx={{ my: 3, mx: 'auto', px: 5, py: 2, bgcolor: '#fff', borderRadius: 5, display: 'inline-block', boxShadow: '0 10px 30px rgba(0,0,0,.12)' }}>
                <Typography sx={{ fontSize: { xs: 90, md: 180 }, fontWeight: 900, letterSpacing: '0.12em', lineHeight: 1.1, fontFamily: 'monospace' }}>{d.code}</Typography>
              </Box>
            )}
            <Typography fontSize={{ xs: 22, md: 34 }} fontWeight={800} mt={2}>출석 {d.count}명</Typography>
            <Typography fontSize={15} sx={{ opacity: 0.8 }}>출석 가능 {hm(d.startAt)} 15분 전 ~ {hm(d.endAt)}</Typography>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};
